import { useState } from "react";

/**
 * Whole-`rawSections` snapshot undo/redo stack management — callers decide
 * WHEN a snapshot lands here, not this hook: one atomic action pushes
 * immediately, while a live-typing field's burst of keystrokes is
 * coalesced into a single push deferred until the field loses focus.
 * Whole snapshots rather than diffs/patches: a full re-derive/re-clone is
 * cheap at SWMM-project scale.
 *
 * `undoStack`/`redoStack` are exposed directly (not just `canUndo`/
 * `canRedo`) so a caller's `useEffect` dependency array can react to every
 * push/pop, not just the 0-1 transition — a keyboard-shortcut listener
 * needs a fresh closure over `undo`/`redo` after every change to either
 * stack.
 */
// Depth-capped so a long editing session can't grow this array without
// bound for the whole browser tab's lifetime.
const MAX_UNDO_DEPTH = 100;

export function useUndoRedo() {
  const [undoStack, setUndoStack] = useState<ReadonlyMap<string, string[][]>[]>([]);
  const [redoStack, setRedoStack] = useState<ReadonlyMap<string, string[][]>[]>([]);

  /** Pushes `snapshot` (the pre-edit `rawSections`) onto the undo stack and clears any pending redo — call before applying the actual edit. Drops the oldest snapshot once the stack would exceed `MAX_UNDO_DEPTH`. */
  function pushUndo(snapshot: ReadonlyMap<string, string[][]>) {
    setUndoStack((stack) => {
      const next = [...stack, snapshot];
      return next.length > MAX_UNDO_DEPTH ? next.slice(next.length - MAX_UNDO_DEPTH) : next;
    });
    setRedoStack([]);
  }

  /** Pops the most recent undo snapshot, pushing `current` onto redo — returns the snapshot to restore, or `undefined` if there's nothing to undo (the caller should no-op in that case). */
  function undo(
    current: ReadonlyMap<string, string[][]>
  ): ReadonlyMap<string, string[][]> | undefined {
    if (undoStack.length === 0) return undefined;
    const previous = undoStack[undoStack.length - 1];
    setUndoStack((stack) => stack.slice(0, -1));
    setRedoStack((stack) => [...stack, current]);
    return previous;
  }

  /** Pops the most recent redo snapshot, pushing `current` back onto undo — returns the snapshot to restore, or `undefined` if there's nothing to redo. */
  function redo(
    current: ReadonlyMap<string, string[][]>
  ): ReadonlyMap<string, string[][]> | undefined {
    if (redoStack.length === 0) return undefined;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((stack) => stack.slice(0, -1));
    setUndoStack((stack) => [...stack, current]);
    return next;
  }

  /** Clears both stacks — history from one project has no meaning applied to another, so every project-switch path (opening/closing/duplicating/deleting) must call this. */
  function reset() {
    setUndoStack([]);
    setRedoStack([]);
  }

  /**
   * Replaces the redo stack outright with `[snapshot]`, without touching
   * `undoStack` — for canceling an in-progress coalesced edit via `undo()`
   * (`useRawSectionEditor.ts`'s own header has the story): committing that
   * edit then immediately popping it back off nets out to exactly this, but
   * computed directly instead of two `setState` calls landing in the right
   * order — `undo()`'s own synchronous return value can't wait for a
   * `pushUndo()` called moments earlier in the same call to actually apply.
   */
  function replaceRedoStack(snapshot: ReadonlyMap<string, string[][]>) {
    setRedoStack([snapshot]);
  }

  return {
    undoStack,
    redoStack,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    pushUndo,
    undo,
    redo,
    reset,
    replaceRedoStack,
  };
}
